import { Dispatch, useCallback, useState } from 'react';
import { SearchResultsType } from '../@types/schema';

interface UseSearchResultsArgs {
  apiHost?: string;
  itemsEngine?: string;
}

const search = async (value, itemsEngine, apiHost) => {
  const SearchMulti = (await import('./search-multi')).default;
  const searchMulti = new SearchMulti({ itemsEngine, apiHost });

  try {
    const results = await searchMulti.search(value);

    return results;
  } catch (ex) {
    if (ex instanceof DOMException) return;
    console.error(ex);

    return 'ERROR';
  }
};

/**
 * Adds drop-in functionality to a component to handle a search event, perform an asynchronous
 * search, and save the results to state. You typically use it like this:
 *
 * ```
 * const [searchResults, handleSearch] = useSearchResults();
 * return <>
 *   <Search onSearch={handleSearch} />
 *   <SearchResults results={searchResults} />
 * </>
 * ```
 *
 * @param {string} args.itemEngine - The engine to use for course search, defaults to `items-st`
 * @returns {Array} [searchResults, handleSearch, setSearchResults]
 */
export default function useSearchResults({
  itemsEngine = process.env.NEXT_PUBLIC_ITEMS_ENGINE,
  apiHost = process.env.NEXT_PUBLIC_VT_API_ENDPOINT,
}: UseSearchResultsArgs = {}): [
  SearchResultsType,
  (value: string) => Promise<void>,
  Dispatch<SearchResultsType>
] {
  const [searchResults, setSearchResults] = useState<SearchResultsType>('EMPTY');

  const handleSearch = useCallback(
    async (value: string) => {
      if (!value) return setSearchResults('EMPTY');
      if (!searchResults) setSearchResults('FETCHING');

      const results = await search(value, itemsEngine, apiHost);
      setSearchResults(results);
    },
    [searchResults, apiHost, itemsEngine]
  );

  return [searchResults, handleSearch, setSearchResults];
}
